import React, { useState, useEffect, useContext, useRef, useMemo } from 'react';
import PageHeader from "@common/PageHeader";
import LessonPlannerCardButton from "./components/LessonPlannerCardButton";
import {useBreadcrumbComponent} from "@common/useBreadcrumbComponent";
import LessonPlannerContentCard from "./components/LessonPlannerContentCard";
import Button from "@common/Button";
import {AddCircleRounded, ArrowBackRounded, ArrowForwardRounded, AutoAwesomeRounded, BarChartRounded, CheckBoxRounded, CheckBoxOutlineBlankRounded, CheckRounded, ChevronRightRounded, CloseRounded, DeleteRounded, DragIndicatorRounded, KeyboardArrowDownRounded, LinkOffRounded, MoreVertRounded, SchoolRounded, KeyboardDoubleArrowLeftRounded, KeyboardDoubleArrowRightRounded} from "@mui/icons-material";
import { Collapse, Dropdown } from 'antd';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, arrayMove } from '@dnd-kit/sortable';
import { NavigationContext } from '@common/context/NavigationContext';
import lessonPlannerEventBus from './lessonPlannerEventBus';
import LessonPlannerClassStatusRow from './components/LessonPlannerClassStatusRow';
import LessonPlannerStatusButton, { STATUS_CONFIG } from './components/LessonPlannerStatusButton';
import PopConfirm from '@common/PopConfirm';
import Tooltip from '@common/Tooltip';
import EditClassesDialog from './components/EditClassesDialog';
import LessonPlanCard from './components/LessonPlanCard';
import AddContentPanel from './components/AddContentPanel';
import Tag from "@common/Tag";
import InputAntdInline from "@common/InputAntdInline";
import axios from '@common/axios';
import CircularLoader from '@common/CircularLoader';
import {useLocation} from "wouter";
import useCurrentShortcut from "@hooks/useCurrentShortcut";

const LessonPlanner = ({ scheduleId }) => {
    const BreadcrumbBar = useBreadcrumbComponent({ dropLast: true });
    const containerRef = useRef(null);
    const addContentButtonRef = useRef(null);
    const { setDrawerComponentName, setDrawerVisible, setDrawerLocation } = useContext(NavigationContext);
    const [, setLocation] = useLocation();
    const { loadShortcutByUrl } = useCurrentShortcut();

    const params = new URLSearchParams(window.location.search);
    const initialUnitId = params.get('unitId') ? parseInt(params.get('unitId')) : null;
    const initialLessonId = params.get('lessonId') ? parseInt(params.get('lessonId')) : null;

    const [plan, setPlan] = useState(null);
    const [units, setUnits] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedUnitId, setSelectedUnitId] = useState(initialUnitId);
    const [selectedLessonId, setSelectedLessonId] = useState(initialLessonId);
    const [mobilePanel, setMobilePanel] = useState(initialLessonId ? 'detail' : initialUnitId ? 'lessons' : 'units');
    const [collapsed, setCollapsed] = useState(false);
    const [renamingSchedule, setRenamingSchedule] = useState(false);
    const [renamingLesson, setRenamingLesson] = useState(false);
    const [addClassDialogVisible, setAddClassDialogVisible] = useState(false);
    const [lessonsSelectMode, setLessonsSelectMode] = useState(false);
    const [selectedLessonIds, setSelectedLessonIds] = useState(new Set());
    const [lastCheckedLessonId, setLastCheckedLessonId] = useState(null);
    const [contentPanelOpen, setContentPanelOpen] = useState(false);
    const [compressedUnits, setCompressedUnits] = useState(false);
    const [renamingUnitId, setRenamingUnitId] = useState(null);
    const [activeUnitKeys, setActiveUnitKeys] = useState([]);

    const toggleLessonSelectMode = () => {
        setLessonsSelectMode(m => {
            if (!m) {
                setSelectedLessonId(null);
                updateUrl(selectedUnitId, null);
            }
            return !m;
        });
        setSelectedLessonIds(new Set());
        setLastCheckedLessonId(null);
    };
    const toggleLessonChecked = (lessonId, shiftKey = false) => {
        const lessons = selectedUnit?.lessons || [];
        if (shiftKey && lastCheckedLessonId) {
            const ids = lessons.map(l => l.lessonId);
            const a = ids.indexOf(lastCheckedLessonId);
            const b = ids.indexOf(lessonId);
            const range = ids.slice(Math.min(a, b), Math.max(a, b) + 1);
            setSelectedLessonIds(prev => new Set([...prev, ...range]));
        } else {
            setSelectedLessonIds(prev => {
                const next = new Set(prev);
                next.has(lessonId) ? next.delete(lessonId) : next.add(lessonId);
                return next;
            });
        }
        setLastCheckedLessonId(lessonId);
    };

    const handleAltCheckboxClick = (e, lessonId) => {
        e.stopPropagation();
        toggleLessonChecked(lessonId);
        selectLesson(null);
    };

    const getUnitForLesson = (lessonId) => units.find(u => u.lessons?.some(l => l.lessonId === lessonId));

    const bulkArchiveLessonsAlt = () => {
        const ids = [...selectedLessonIds];
        Promise.all(ids.map(lessonId => {
            const unit = getUnitForLesson(lessonId);
            return unit ? axios.post(`/web/ui/lessonScheduler/units/${unit.unitId}/lessons/${lessonId}/archive`) : Promise.resolve();
        })).then(() => {
            setUnits(prev => prev.map(u => ({ ...u, lessons: (u.lessons || []).filter(l => !ids.includes(l.lessonId)) })));
            if (ids.includes(selectedLessonId)) setSelectedLessonId(null);
            setSelectedLessonIds(new Set());
            displayToastMessage('Lessons deleted', `${ids.length} lesson${ids.length !== 1 ? 's' : ''} deleted`, 'success');
        });
    };

    const bulkUpdateStatusAlt = (state) => {
        const count = selectedLessonIds.size;
        [...selectedLessonIds].forEach(lessonId => {
            const unit = getUnitForLesson(lessonId);
            if (unit) updateLessonState(unit.unitId, lessonId, state);
        });
        setSelectedLessonIds(new Set());
        displayToastMessage('Status updated', `${count} lesson${count !== 1 ? 's' : ''} updated`, 'success');
    };

    const renameSchedule = (name) => {
        setRenamingSchedule(true);
        axios.post(`/web/ui/lessonScheduler/plans/${scheduleId}`, { name, description: plan.description })
            .then(() => setPlan(prev => ({ ...prev, name })))
            .finally(() => setRenamingSchedule(false));
    };

    const updateUrl = (unitId, lessonId) => {
        const search = new URLSearchParams();
        if (unitId) search.set('unitId', unitId);
        if (lessonId) search.set('lessonId', lessonId);
        const qs = search.toString();
        setLocation(`${window.location.pathname}${qs ? `?${qs}` : ''}`);
    };

    useEffect(() => {
        const el = containerRef.current;
        if (!el) return;
        const topOffset = el.getBoundingClientRect().top + window.scrollY;
        const setHeight = () => {
            if (window.innerWidth > 767) {
                el.style.height = `${window.innerHeight - topOffset - 16}px`;
            } else {
                el.style.height = '';
            }
        };
        setHeight();
        window.addEventListener('resize', setHeight);
        return () => window.removeEventListener('resize', setHeight);
    }, []);

    useEffect(() => {
        setLoading(true);
        axios.get(`/web/ui/lessonScheduler/plans/${scheduleId}`)
            .then(r => {
                setPlan(r.data);
                setUnits(r.data.units || []);
                setActiveUnitKeys((r.data.units || []).map(u => String(u.unitId)));
            })
            .finally(() => setLoading(false));
    }, [scheduleId]);

    const selectedUnit = units.find(u => u.unitId === selectedUnitId);
    const selectedLesson = selectedUnit?.lessons?.find(l => l.lessonId === selectedLessonId);
    const selectedLessonContent = (selectedLesson?.concepts || []).map(c => ({ id: String(c.conceptId), title: c.conceptName }));
    const hasLinkedLessonPlan = selectedLesson?.lessonPlanId != null;

    const conceptUsageMap = useMemo(() => {
        const map = {};
        let lessonNumber = 0;
        units.forEach(unit => {
            (unit.lessons || []).forEach(lesson => {
                lessonNumber++;
                (lesson.concepts || []).forEach(concept => {
                    const id = String(concept.conceptId);
                    if (!map[id]) map[id] = [];
                    map[id].push({ lessonId: lesson.lessonId, lessonName: lesson.name, lessonNumber });
                });
            });
        });
        return map;
    }, [units]);

    const altSelectMode = selectedLessonIds.size > 0;

    // DEMO HACK: show lesson plan card only on the first lesson that has concepts. Replace with real per-lesson data when backend is ready.

    const selectUnit = (unitId) => {
        setSelectedUnitId(unitId);
        setSelectedLessonId(null);
        setMobilePanel('lessons');
        setCollapsed(false);
        updateUrl(unitId, null);
    };

    const selectLesson = (lessonId) => {
        setSelectedLessonId(lessonId);
        setMobilePanel('detail');
        setCollapsed(false);
        updateUrl(selectedUnitId, lessonId);
    };

    const addUnit = () => {
        const seq = units.length;
        axios.post(`/web/ui/lessonScheduler/plans/${scheduleId}/units`, { name: `Unit ${seq + 1}`, seq })
            .then(r => {
                setUnits(prev => [...prev, { ...r.data, lessons: r.data.lessons || [] }]);
                setActiveUnitKeys(prev => [...prev, String(r.data.unitId)]);
            });
    };

    const renameUnit = (unitId, name) => {
        const unit = units.find(u => u.unitId === unitId);
        if (!unit) return;
        axios.post(`/web/ui/lessonScheduler/plans/${scheduleId}/units/${unitId}`, {
            name, description: unit.description, seq: unit.seq,
        }).then(() => setUnits(prev => prev.map(u => u.unitId === unitId ? { ...u, name } : u)));
    };

    const archiveUnit = (unitId) => {
        axios.post(`/web/ui/lessonScheduler/plans/${scheduleId}/units/${unitId}/archive`)
            .then(() => {
                setUnits(prev => prev.filter(u => u.unitId !== unitId));
                if (selectedUnitId === unitId) {
                    setSelectedUnitId(null);
                    setSelectedLessonId(null);
                }
            });
    };

    const addLessonToUnit = (unitId) => {
        const unit = units.find(u => u.unitId === unitId);
        const seq = unit?.lessons?.length || 0;
        axios.post(`/web/ui/lessonScheduler/units/${unitId}/lessons`, { name: `Lesson ${seq + 1}`, seq })
            .then(r => {
                setUnits(prev => prev.map(u => {
                    if (u.unitId !== unitId) return u;
                    return { ...u, lessons: [...(u.lessons || []), r.data] };
                }));
            });
    };

    const addLesson = () => {
        if (!selectedUnitId) return;
        addLessonToUnit(selectedUnitId);
    };

    const renameLesson = (unitId, lessonId, name) => {
        const unit = units.find(u => u.unitId === unitId);
        const lesson = unit?.lessons?.find(l => l.lessonId === lessonId);
        if (!lesson) return;
        setRenamingLesson(true);
        axios.post(`/web/ui/lessonScheduler/units/${unitId}/lessons/${lessonId}`, {
            name, description: lesson.description, seq: lesson.seq, lessonType: lesson.lessonType,
        }).then(() => setUnits(prev => prev.map(u => {
            if (u.unitId !== unitId) return u;
            return { ...u, lessons: (u.lessons || []).map(l => l.lessonId === lessonId ? { ...l, name } : l) };
        }))).finally(() => setRenamingLesson(false));
    };

    const archiveLesson = (unitId, lessonId) => {
        axios.post(`/web/ui/lessonScheduler/units/${unitId}/lessons/${lessonId}/archive`)
            .then(() => {
                setUnits(prev => prev.map(u => {
                    if (u.unitId !== unitId) return u;
                    return { ...u, lessons: (u.lessons || []).filter(l => l.lessonId !== lessonId) };
                }));
                if (selectedLessonId === lessonId) setSelectedLessonId(null);
            });
    };

    const bulkArchiveLessons = () => {
        const ids = [...selectedLessonIds];
        Promise.all(ids.map(lessonId =>
            axios.post(`/web/ui/lessonScheduler/units/${selectedUnitId}/lessons/${lessonId}/archive`)
        )).then(() => {
            setUnits(prev => prev.map(u => {
                if (u.unitId !== selectedUnitId) return u;
                return { ...u, lessons: (u.lessons || []).filter(l => !ids.includes(l.lessonId)) };
            }));
            if (ids.includes(selectedLessonId)) setSelectedLessonId(null);
            setSelectedLessonIds(new Set());
            toggleLessonSelectMode();
            displayToastMessage('Lessons deleted', `${ids.length} lesson${ids.length !== 1 ? 's' : ''} deleted`, 'success');
        });
    };

    const setLessonStatus = (lessonState, scheduleClassId) => {
        if (!selectedLessonId || !selectedUnitId) return;
        updateLessonState(selectedUnitId, selectedLessonId, lessonState, scheduleClassId);
    };

    const updateLessonState = (unitId, lessonId, lessonState, scheduleClassId) => {
        const body = scheduleClassId ? { lessonState, scheduleClassId } : { lessonState };
        axios.post(`/web/ui/lessonScheduler/units/${unitId}/lessons/${lessonId}/state`, body)
            .then(r => {
                setUnits(prev => prev.map(u => ({
                    ...u,
                    lessons: (u.lessons || []).map(l => l.lessonId === lessonId ? r.data : l),
                })));
            });
    };


    const sensors = useSensors(useSensor(PointerSensor));

    const onDragEndUnits = ({ active, over }) => {
        if (!over || active.id === over.id) return;
        setUnits(prev => {
            const oldIndex = prev.findIndex(u => u.unitId === active.id);
            const newIndex = prev.findIndex(u => u.unitId === over.id);
            const reordered = arrayMove(prev, oldIndex, newIndex).map((u, i) => ({ ...u, seq: i }));
            reordered.forEach(u => {
                axios.post(`/web/ui/lessonScheduler/plans/${scheduleId}/units/${u.unitId}`, {
                    name: u.name, description: u.description, seq: u.seq,
                });
            });
            return reordered;
        });
    };

    const onDragEndLessons = ({ active, over }) => {
        if (!over || active.id === over.id) return;
        setUnits(prev => prev.map(u => {
            if (u.unitId !== selectedUnitId) return u;
            const oldIndex = u.lessons.findIndex(l => l.lessonId === active.id);
            const newIndex = u.lessons.findIndex(l => l.lessonId === over.id);
            const reordered = arrayMove(u.lessons, oldIndex, newIndex).map((l, i) => ({ ...l, seq: i }));
            reordered.forEach(l => {
                axios.post(`/web/ui/lessonScheduler/units/${u.unitId}/lessons/${l.lessonId}`, {
                    name: l.name, description: l.description, seq: l.seq, lessonType: l.lessonType,
                });
            });
            return { ...u, lessons: reordered };
        }));
    };

    const addContentToLesson = (item) => {
        if (!selectedLessonId || !selectedUnitId) return;
        const unit = units.find(u => u.unitId === selectedUnitId);
        const lesson = unit?.lessons?.find(l => l.lessonId === selectedLessonId);
        const shouldAutoRename = lesson && (lesson.concepts || []).length === 0 && /^Lesson \d+$/.test(lesson.name);

        axios.post(`/web/ui/lessonScheduler/units/${selectedUnitId}/lessons/${selectedLessonId}/concepts`, {
            conceptId: parseInt(item.id),
        }).then(r => {
            setUnits(prev => prev.map(u => ({
                ...u,
                lessons: (u.lessons || []).map(l => l.lessonId === selectedLessonId ? r.data : l),
            })));
            setTimeout(() => addContentButtonRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' }), 0);
            if (shouldAutoRename) {
                axios.post(`/web/ui/lessonScheduler/units/${selectedUnitId}/lessons/${selectedLessonId}`, {
                    name: item.title, description: lesson.description, seq: lesson.seq, lessonType: lesson.lessonType,
                }).then(() => setUnits(prev => prev.map(u => {
                    if (u.unitId !== selectedUnitId) return u;
                    return { ...u, lessons: (u.lessons || []).map(l => l.lessonId === selectedLessonId ? { ...l, name: item.title } : l) };
                })));
            }
        });
    };

    const removeContentFromLesson = (itemId) => {
        if (!selectedLessonId || !selectedUnitId) return;
        axios.delete(`/web/ui/lessonScheduler/units/${selectedUnitId}/lessons/${selectedLessonId}/concepts/${itemId}`)
            .then(() => {
                setUnits(prev => prev.map(u => ({
                    ...u,
                    lessons: (u.lessons || []).map(l => {
                        if (l.lessonId !== selectedLessonId) return l;
                        return { ...l, concepts: (l.concepts || []).filter(c => String(c.conceptId) !== String(itemId)) };
                    }),
                })));
            });
    };

    const onClassAdded = () => {
        axios.get(`/web/ui/lessonScheduler/plans/${scheduleId}`)
            .then(r => {
                setPlan(r.data);
                setUnits(r.data.units || []);
            });
    };

    const openReportDrawer = () => {
        lessonPlannerEventBus.scheduleId = scheduleId;
        setDrawerComponentName('LessonPlannerReportDrawer');
        setDrawerLocation('right');
        setDrawerVisible(true);
    };

    const openAddContentDrawer = () => {
        setContentPanelOpen(true);
        setCollapsed(true);
    };

    const generateLessonPlan = () => {
        if (!selectedLessonId || !selectedLessonContent.length) return;

        // Navigate to wizard with lesson ID as query parameter
        loadShortcutByUrl(`/web/ui/view/lessonStudio/lessonPlans/new?lessonId=${selectedLessonId}`);
    };

    const unlinkLessonPlan = () => {
        if (!selectedLessonId) return;

        axios.delete(`/web/ui/lessonPlanner/lessons/${selectedLessonId}/unlinkLessonPlan`)
            .then(() => {
                displayToastMessage('Unlinked', 'Lesson plan has been unlinked from this lesson', 'success');
                // Refresh the schedule to update the UI
                return axios.get(`/web/ui/lessonScheduler/plans/${scheduleId}`);
            })
            .then(r => {
                if (r) {
                    setPlan(r.data);
                    setUnits(r.data.units || []);
                }
            })
            .catch(error => {
                console.error(error);
                displayToastMessage('Failed to unlink', error.response?.data?.message || 'Failed to unlink lesson plan', 'error');
            });
    };

    return (
        <div className="ch-lesson-planner" ref={containerRef}>
        {loading && <CircularLoader pageCentered={true} />}
        {!loading && <>
            {BreadcrumbBar && <BreadcrumbBar />}
            <PageHeader
                title={
                    <InputAntdInline
                        value={plan?.name ?? ''}
                        onSave={renameSchedule}
                        isLoading={renamingSchedule}
                        fieldLabel="Schedule name"
                    />
                }
                subtitle={plan?.classNames?.length > 0 && (
                    <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                        {plan.classNames.map(name => <Tag key={name}>{name}</Tag>)}
                    </div>
                )}
                buttons={
                    <div style={{ display: 'flex', gap: '8px' }}>
                        <Button type="outline" icon={compressedUnits ? <CheckBoxRounded /> : <CheckBoxOutlineBlankRounded />} onClick={() => setCompressedUnits(v => !v)}>Alt</Button>
                        <Button type="outline" icon={<BarChartRounded />} onClick={openReportDrawer}>Report</Button>
                        <Button type="outline" icon={<SchoolRounded />} onClick={() => setAddClassDialogVisible(true)}>Edit Classes</Button>
                    </div>
                }
            />
            <EditClassesDialog
                scheduleId={scheduleId}
                visible={addClassDialogVisible}
                handleClose={() => setAddClassDialogVisible(false)}
                plan={plan}
                onSuccess={onClassAdded}
            />

            {compressedUnits ? (
                <div className="ch-lesson-planner-alt-view">
                    <div className={`ch-lesson-planner-alt-left${altSelectMode ? ' ch-lesson-planner-alt-left--selecting' : ''}`}>
                        <div className="ch-lesson-planner-alt-left-scroll">
                        <Collapse
                            activeKey={activeUnitKeys}
                            onChange={setActiveUnitKeys}
                            items={units.map((unit, unitIndex) => ({
                                key: String(unit.unitId),
                                label: (
                                    <span className="ch-lesson-planner-alt-unit-label">
                                        {renamingUnitId === unit.unitId ? (
                                            <span className="ch-lesson-planner-alt-unit-editing">
                                                <InputAntdInline
                                                    value={unit.name}
                                                    isEditing={true}
                                                    onSave={(name) => { renameUnit(unit.unitId, name); setRenamingUnitId(null); }}
                                                    onCancel={() => setRenamingUnitId(null)}
                                                    fieldLabel="Unit name"
                                                    textType="span"
                                                />
                                            </span>
                                        ) : (
                                            <button type="button" className="ch-lesson-planner-alt-unit-label-toggle" onClick={() => setActiveUnitKeys(prev => prev.includes(String(unit.unitId)) ? prev.filter(k => k !== String(unit.unitId)) : [...prev, String(unit.unitId)])}>
                                                <span className="ch-lesson-planner-alt-unit-label-name">{unit.name}</span>
                                                <span className="ch-lesson-planner-alt-unit-label-sub">Unit {unitIndex + 1} · {(unit.lessons || []).length} Lesson{(unit.lessons || []).length !== 1 ? 's' : ''}</span>
                                            </button>
                                        )}
                                        {renamingUnitId !== unit.unitId && (
                                            <Dropdown
                                                menu={{ items: [
                                                    { key: 'rename', label: 'Rename Unit' },
                                                    { key: 'delete', label: 'Delete Unit', danger: true },
                                                ], onClick: ({ key }) => {
                                                    if (key === 'rename') setRenamingUnitId(unit.unitId);
                                                    if (key === 'delete') archiveUnit(unit.unitId);
                                                }}}
                                                trigger={['click']}
                                                placement="bottomRight"
                                            >
                                                <Button type="text" icon={<MoreVertRounded style={{ fontSize: 18 }} />} />
                                            </Dropdown>
                                        )}
                                    </span>
                                ),
                                children: (
                                    <>
                                    <ul className="ch-lesson-planner-alt-lesson-rows">
                                        {(unit.lessons || []).map((lesson, lessonIndex) => {
                                            const lowestStatus = lesson.classStatuses?.length
                                                ? ['PLANNED', 'ACTIVE', 'COMPLETE'].find(s => lesson.classStatuses.some(cs => cs.lessonState === s))
                                                : null;
                                            const dotColor = lowestStatus ? STATUS_CONFIG[lowestStatus].color : '#ccc';
                                            const isSelected = selectedLessonId === lesson.lessonId;
                                            const isChecked = selectedLessonIds.has(lesson.lessonId);
                                            return (
                                                <li key={lesson.lessonId}>
                                                    <div
                                                        className={`ch-lesson-planner-alt-lesson-row${isSelected ? ' ch-lesson-planner-alt-lesson-row--selected' : ''}${isChecked ? ' ch-lesson-planner-alt-lesson-row--checked' : ''}`}
                                                        onClick={altSelectMode ? () => toggleLessonChecked(lesson.lessonId) : () => { selectUnit(unit.unitId); selectLesson(lesson.lessonId); }}
                                                        role="button"
                                                        tabIndex={0}
                                                        onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); altSelectMode ? toggleLessonChecked(lesson.lessonId) : (selectUnit(unit.unitId), selectLesson(lesson.lessonId)); } }}
                                                    >
                                                        <span className="ch-lesson-planner-card-drag-handle" aria-hidden="true" onClick={e => e.stopPropagation()}>
                                                            <DragIndicatorRounded style={{ fontSize: '18px' }} />
                                                        </span>
                                                        <span className="ch-lesson-planner-alt-lesson-row-indicator">
                                                            <span
                                                                className="ch-lesson-planner-card-checkbox"
                                                                aria-hidden="true"
                                                                onClick={e => handleAltCheckboxClick(e, lesson.lessonId)}
                                                            >
                                                                {isChecked && <CheckRounded style={{ fontSize: '14px' }} />}
                                                            </span>
                                                            <span aria-hidden="true" className="ch-lesson-planner-alt-lesson-row-dot" style={{ backgroundColor: dotColor }} />
                                                        </span>
                                                        <div className="ch-lesson-planner-alt-lesson-row-text">
                                                            <span className="ch-lesson-planner-alt-lesson-row-title">{lesson.name}</span>
                                                            <span className="ch-lesson-planner-alt-lesson-row-subtitle">Lesson {lessonIndex + 1}</span>
                                                        </div>
                                                        <span className="ch-lesson-planner-alt-lesson-row-chevron" aria-hidden="true">
                                                            <ChevronRightRounded style={{ fontSize: '18px' }} />
                                                        </span>
                                                    </div>
                                                </li>
                                            );
                                        })}
                                    </ul>
                                    <div className="ch-lesson-planner-alt-add-lesson">
                                        <Button type="dashed" icon={<AddCircleRounded />} onClick={() => addLessonToUnit(unit.unitId)}>Add Lesson</Button>
                                    </div>
                                    </>
                                ),
                            }))}
                            className="ch-lesson-planner-content-collapse"
                            collapsible="icon"
                            expandIconPlacement="end"
                            expandIcon={({ isActive }) => (
                                <KeyboardArrowDownRounded style={{
                                    fontSize: '18px',
                                    transform: isActive ? 'rotate(180deg)' : 'rotate(0deg)',
                                    transition: 'transform 0.2s',
                                }} />
                            )}
                        />
                        </div>
                        <div className="ch-lesson-planner-alt-left-footer">
                            <button className="ch-lesson-planner-alt-add-unit" onClick={addUnit}>
                                <AddCircleRounded style={{ fontSize: '18px' }} />
                                Add Unit
                            </button>
                        </div>
                        <div className={`ch-lesson-planner-alt-bulk-bar${selectedLessonIds.size > 0 ? ' ch-lesson-planner-alt-bulk-bar--visible' : ''}`}>
                            <span className="ch-lesson-planner-alt-bulk-bar-count">{selectedLessonIds.size} selected</span>
                            <div className="ch-lesson-planner-alt-bulk-bar-actions">
                                <LessonPlannerStatusButton placeholder="Set status" onChange={bulkUpdateStatusAlt} />
                                <PopConfirm
                                    title="Delete selected lessons?"
                                    okText="Delete"
                                    cancelText="Cancel"
                                    onConfirm={bulkArchiveLessonsAlt}
                                >
                                    <>
                                        <Tooltip title="Delete selected">
                                            <Button type="outline" aria-label="Delete selected lessons">
                                                <DeleteRounded style={{ fontSize: '18px' }} />
                                            </Button>
                                        </Tooltip>
                                    </>
                                </PopConfirm>
                                <Tooltip title="Clear selection">
                                    <Button type="text" aria-label="Clear selection" onClick={() => setSelectedLessonIds(new Set())}>
                                        <CloseRounded style={{ fontSize: '18px' }} />
                                    </Button>
                                </Tooltip>
                            </div>
                        </div>
                    </div>
                    <div className="ch-lesson-planner-alt-right">
                        <div className="ch-lesson-planner-detail-header">
                            <div className="ch-lesson-planner-detail-title-row">
                                <h3 className="ch-lesson-planner-panel-heading-lesson">
                                    {selectedLesson ? (
                                        <InputAntdInline
                                            value={selectedLesson.name}
                                            textType="span"
                                            onSave={(name) => renameLesson(selectedUnitId, selectedLessonId, name)}
                                            isLoading={renamingLesson}
                                            fieldLabel="Lesson name"
                                            minWidth={150}
                                            fullWidth
                                        />
                                    ) : 'Lesson Detail'}
                                </h3>
                                {selectedLesson && selectedLessonContent.length > 0 && (
                                    <div className="ch-lesson-planner-heading-actions">
                                        {hasLinkedLessonPlan ? (
                                            <>
                                                <Button type={'outline'} icon={<ArrowForwardRounded />} iconPosition="right" onClick={() => loadShortcutByUrl(`/web/ui/view/lessonStudio/lessonPlans/plan/${selectedLesson.lessonPlanId}`)}>Open lesson plan</Button>
                                                <PopConfirm
                                                    title="Unlink lesson plan?"
                                                    description="This will remove the link between this lesson and the lesson plan. The lesson plan will not be deleted."
                                                    onConfirm={unlinkLessonPlan}
                                                    okText="Unlink"
                                                    cancelText="Cancel"
                                                >
                                                    <Button type={'outline'} icon={<LinkOffRounded />} />
                                                </PopConfirm>
                                            </>
                                        ) : (
                                            <Button type={'outline'} icon={<AutoAwesomeRounded />} onClick={generateLessonPlan}>Generate lesson plan</Button>
                                        )}
                                    </div>
                                )}
                            </div>
                            {selectedLesson && selectedLessonContent.length > 0 && (selectedLesson.classStatuses || []).length > 0 && (
                                <LessonPlannerClassStatusRow
                                    classStatuses={selectedLesson.classStatuses}
                                    onStatusChange={setLessonStatus}
                                />
                            )}
                        </div>
                        <div className="ch-lesson-planner-detail-body">
                            {selectedLesson ? (
                                <>
                                    {hasLinkedLessonPlan && (
                                        <div className="ch-lesson-planner-detail-section">
                                            <h4 className="ch-lesson-planner-detail-section-heading">Lesson plan</h4>
                                            <div className="ch-lesson-planner-panel-content">
                                                <LessonPlanCard
                                                    name={selectedLesson.name}
                                                    description={selectedLesson.description || 'AI-generated lesson plan'}
                                                    tags={['AI Generated']}
                                                    onOpen={() => loadShortcutByUrl(`/web/ui/view/lessonStudio/lessonPlans/plan/${selectedLesson.lessonPlanId}`)}
                                                />
                                            </div>
                                        </div>
                                    )}
                                    <div className="ch-lesson-planner-detail-section">
                                        <h4 className="ch-lesson-planner-detail-section-heading">Sections covered in this lesson</h4>
                                        <div className="ch-lesson-planner-panel-content">
                                            {selectedLessonContent.map(item => (
                                                <LessonPlannerContentCard
                                                    key={item.id}
                                                    title={item.title}
                                                    onDelete={() => removeContentFromLesson(item.id)}
                                                />
                                            ))}
                                            <Button ref={addContentButtonRef} type={'dashed'} icon={<AddCircleRounded />} onClick={openAddContentDrawer}>Add Lesson Content</Button>
                                        </div>
                                    </div>
                                </>
                            ) : (
                                <p className="ch-lesson-planner-empty-message">Select a lesson to view its content</p>
                            )}
                        </div>
                    </div>
                    <AddContentPanel
                        open={contentPanelOpen}
                        onClose={() => setContentPanelOpen(false)}
                        selectedLessonContent={selectedLessonContent}
                        selectedLessonId={selectedLessonId}
                        conceptUsageMap={conceptUsageMap}
                        onAdd={addContentToLesson}
                    />
                </div>
            ) : (
            <div className={`ch-lesson-planner-layout${contentPanelOpen ? ' ch-lesson-planner-layout--content-open' : ''}`}>
                <div className={`ch-lesson-planner-sidebar${collapsed ? ' ch-lesson-planner-sidebar--collapsed' : ''}`}>
                    <div className={`ch-lesson-planner-units-panel${mobilePanel === 'units' ? ' ch-lesson-planner-panel--active' : ''}`}>
                        <div className="ch-lesson-planner-panel-heading-row">
                            <h3 className="ch-lesson-planner-panel-heading">Units</h3>
                        </div>
                        <div className="ch-lesson-planner-panel-content">
                            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEndUnits}>
                                <SortableContext items={units.map(u => u.unitId)} strategy={verticalListSortingStrategy}>
                                    {units.map((unit, i) => (
                                        <LessonPlannerCardButton
                                            key={unit.unitId}
                                            id={unit.unitId}
                                            title={unit.name}
                                            subtitle={`Unit ${i + 1} · ${(unit.lessons || []).length} Lesson${(unit.lessons || []).length !== 1 ? 's' : ''}`}

                                            selected={selectedUnitId === unit.unitId}
                                            onClick={() => selectUnit(unit.unitId)}
                                            onArchive={() => archiveUnit(unit.unitId)}
                                            onRename={(name) => renameUnit(unit.unitId, name)}
                                            lessons={unit.lessons || []}
                                        />
                                    ))}
                                </SortableContext>
                            </DndContext>
                            <Button type={'dashed'} icon={<AddCircleRounded />} onClick={addUnit}>Add Unit</Button>
                        </div>
                    </div>

                    <div className={`ch-lesson-planner-lessons-panel${mobilePanel === 'lessons' ? ' ch-lesson-planner-panel--active' : ''}`}>
                        <div className="ch-lesson-planner-panel-heading-row">
                            <Button type="text" className="ch-lesson-planner-mobile-back" aria-label="Back to units" onClick={() => setMobilePanel('units')}>
                                <ArrowBackRounded style={{ fontSize: '18px' }} />
                            </Button>
                            {lessonsSelectMode
                                ? <LessonPlannerStatusButton placeholder="Set status" disabled={selectedLessonIds.size === 0} onChange={(state) => {
                                        const count = selectedLessonIds.size;
                                        selectedLessonIds.forEach(lessonId => updateLessonState(selectedUnitId, lessonId, state));
                                        toggleLessonSelectMode();
                                        displayToastMessage('Status updated', `${count} lesson${count !== 1 ? 's' : ''} set to ${state.charAt(0) + state.slice(1).toLowerCase()}`, 'success');
                                    }} />
                                : <h3 className="ch-lesson-planner-panel-heading">Lessons</h3>
                            }
                            {selectedUnit && (
                                <div style={{ display: 'flex', gap: '4px' }}>
                                    {lessonsSelectMode && (
                                        <PopConfirm
                                            title="Delete selected lessons?"
                                            okText="Delete"
                                            cancelText="Cancel"
                                            disabled={selectedLessonIds.size === 0}
                                            onConfirm={bulkArchiveLessons}
                                        >
                                            <>
                                                <Tooltip title="Delete selected">
                                                    <Button type="outline" aria-label="Delete selected lessons" disabled={selectedLessonIds.size === 0}>
                                                        <DeleteRounded style={{ fontSize: '18px' }} />
                                                    </Button>
                                                </Tooltip>
                                            </>
                                        </PopConfirm>
                                    )}
                                    <Tooltip title={lessonsSelectMode ? 'Cancel selection' : 'Perform bulk actions'}>
                                        <Button type="outline" aria-label={lessonsSelectMode ? 'Cancel selection' : 'Select lessons'} onClick={toggleLessonSelectMode}>
                                            {lessonsSelectMode ? <CloseRounded style={{ fontSize: '18px' }} /> : 'Bulk'}
                                        </Button>
                                    </Tooltip>
                                </div>
                            )}
                        </div>
                        <div className="ch-lesson-planner-panel-content">
                            {selectedUnit ? (
                                <>
                                    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEndLessons}>
                                        <SortableContext items={(selectedUnit.lessons || []).map(l => l.lessonId)} strategy={verticalListSortingStrategy}>
                                            {(selectedUnit.lessons || []).map((lesson, i) => (
                                                <LessonPlannerCardButton
                                                    key={lesson.lessonId}
                                                    id={lesson.lessonId}
                                                    title={lesson.name}
                                                    subtitle={`Lesson ${i + 1}`}

                                                    selected={selectedLessonId === lesson.lessonId}
                                                    onClick={() => selectLesson(lesson.lessonId)}
                                                    onArchive={() => archiveLesson(selectedUnitId, lesson.lessonId)}
                                                    onRename={(name) => renameLesson(selectedUnitId, lesson.lessonId, name)}
                                                    classStatuses={lesson.classStatuses}
                                                    selectMode={lessonsSelectMode}
                                                    checked={selectedLessonIds.has(lesson.lessonId)}
                                                    onToggle={(e) => toggleLessonChecked(lesson.lessonId, e?.shiftKey)}
                                                />
                                            ))}
                                        </SortableContext>
                                    </DndContext>
                                    <Button type={'dashed'} icon={<AddCircleRounded />} onClick={addLesson}>Add Lesson</Button>
                                </>
                            ) : (
                                <p className="ch-lesson-planner-empty-message">Select a unit to see its lessons</p>
                            )}
                        </div>
                    </div>
                </div>

                <div className="ch-lesson-planner-collapse-track">
                    <button
                        className="ch-lesson-planner-collapse-btn"
                        onClick={() => setCollapsed(c => { if (c) setContentPanelOpen(false); return !c; })}
                        title={collapsed ? 'Expand panels' : 'Collapse panels'}
                    >
                        {collapsed
                            ? <KeyboardDoubleArrowRightRounded style={{ fontSize: '14px' }} />
                            : <KeyboardDoubleArrowLeftRounded style={{ fontSize: '14px' }} />
                        }
                    </button>
                </div>

                <div className={`ch-lesson-planner-detail-panel${mobilePanel === 'detail' ? ' ch-lesson-planner-panel--active' : ''}`}>
                    <div className="ch-lesson-planner-detail-header">
                        <div className="ch-lesson-planner-detail-title-row">
                            <Button type="text" className="ch-lesson-planner-mobile-back" aria-label="Back to lessons" onClick={() => setMobilePanel('lessons')}>
                                <ArrowBackRounded style={{ fontSize: '18px' }} />
                            </Button>
                            <h3 className="ch-lesson-planner-panel-heading-lesson">
                                {selectedLesson ? (
                                    <InputAntdInline
                                        value={selectedLesson.name}
                                        textType="span"
                                        onSave={(name) => renameLesson(selectedUnitId, selectedLessonId, name)}
                                        isLoading={renamingLesson}
                                        fieldLabel="Lesson name"
                                        minWidth={150}
                                        fullWidth
                                    />
                                ) : 'Lesson Detail'}
                            </h3>
                            {selectedLesson && selectedLessonContent.length > 0 && (
                                <div className="ch-lesson-planner-heading-actions">
                                    {hasLinkedLessonPlan ? (
                                        <>
                                            <Button type={'outline'} icon={<ArrowForwardRounded />} iconPosition="right" onClick={() => loadShortcutByUrl(`/web/ui/view/lessonStudio/lessonPlans/plan/${selectedLesson.lessonPlanId}`)}>Open lesson plan</Button>
                                            <PopConfirm
                                                title="Unlink lesson plan?"
                                                description="This will remove the link between this lesson and the lesson plan. The lesson plan will not be deleted."
                                                onConfirm={unlinkLessonPlan}
                                                okText="Unlink"
                                                cancelText="Cancel"
                                            >
                                                <Button type={'outline'} icon={<LinkOffRounded />} />
                                            </PopConfirm>
                                        </>
                                    ) : (
                                        <Button type={'outline'} icon={<AutoAwesomeRounded />} onClick={generateLessonPlan}>Generate lesson plan</Button>
                                    )}
                                </div>
                            )}
                        </div>
                        {selectedLesson && selectedLessonContent.length > 0 && (selectedLesson.classStatuses || []).length > 0 && (
                            <LessonPlannerClassStatusRow
                                classStatuses={selectedLesson.classStatuses}
                                onStatusChange={setLessonStatus}
                            />
                        )}
                    </div>
                    <div className="ch-lesson-planner-detail-body">
                        {selectedLesson ? (
                            <>
                                {hasLinkedLessonPlan && (
                                    <div className="ch-lesson-planner-detail-section">
                                        <h4 className="ch-lesson-planner-detail-section-heading">Lesson plan</h4>
                                        <div className="ch-lesson-planner-panel-content">
                                            <LessonPlanCard
                                                name={selectedLesson.name}
                                                description={selectedLesson.description || 'AI-generated lesson plan'}
                                                tags={['AI Generated']}
                                                onOpen={() => loadShortcutByUrl(`/web/ui/view/lessonStudio/lessonPlans/plan/${selectedLesson.lessonPlanId}`)}
                                            />
                                        </div>
                                    </div>
                                )}
                                <div className="ch-lesson-planner-detail-section">
                                    <h4 className="ch-lesson-planner-detail-section-heading">Sections covered in this lesson</h4>
                                    <div className="ch-lesson-planner-panel-content">
                                        {selectedLessonContent.map(item => (
                                            <LessonPlannerContentCard
                                                key={item.id}
                                                title={item.title}
                                                onDelete={() => removeContentFromLesson(item.id)}
                                            />
                                        ))}
                                        <Button ref={addContentButtonRef} type={'dashed'} icon={<AddCircleRounded />} onClick={openAddContentDrawer}>Add Lesson Content</Button>
                                    </div>
                                </div>
                            </>
                        ) : (
                            <p className="ch-lesson-planner-empty-message">Select a lesson to view its content</p>
                        )}
                    </div>
                </div>
                <AddContentPanel
                    open={contentPanelOpen}
                    onClose={() => setContentPanelOpen(false)}
                    selectedLessonContent={selectedLessonContent}
                    selectedLessonId={selectedLessonId}
                    conceptUsageMap={conceptUsageMap}
                    onAdd={addContentToLesson}
                />
            </div>
            )}
        </>}
        </div>
    );
};

export default LessonPlanner;
